import * as React from "react";
import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import Delete from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import { formatTimeWithAMPM, formatToUsDateFromUTCNew } from "../../../utils/DateHelper";
import { AuthenticationContext, ImagesContext } from "../../../Context";
import { getHeaders } from "../../../utils/HeaderHelper";
import { useParams } from "react-router-dom";
import AddMedication from "./AddMedication";
import MedicationListHistoryModal from "./MedicationListHistoryModal";

interface Props {}

const TABLE_MAX_HEIGHT = 420;
const DIRECTIONS_MAX_LENGTH = 500;

const subtextStyle: React.CSSProperties = {
  margin: 0,
  color: "var(--med-grey, #4A4442)",
  fontFeatureSettings: "'liga' off, 'clig' off",
  fontFamily: "QuicksandMedium",
  fontSize: "12px",
  fontStyle: "normal",
  fontWeight: 500,
  lineHeight: 1.2,
};

const darkHeaderStyle: React.CSSProperties = {
  margin: 0,
  color: "#1E1E1E",
  fontFamily: "QuicksandBold",
  fontWeight: 700,
};

const Medications: React.FC<Props> = () => {
  const authenticationSetting = React.useContext<any>(AuthenticationContext);
  const images = React.useContext(ImagesContext);
  const [medications, setMedications] = React.useState<any[]>([]);
  const [outsideMedications, setOutsideMedications] = React.useState<any[]>([]);
  const [outsideMedicationSearch, setOutsideMedicationSearch] = React.useState("");
  const [outsideMedicationsSearched, setOutsideMedicationsSearched] =
    React.useState(false);
  const [isReconciliationMode, setIsReconciliationMode] = React.useState(false);
  const [hasReconciliationChanges, setHasReconciliationChanges] = React.useState(false);
  const [reconciledAt, setReconciledAt] = React.useState<string | null>(null);
  const [reconciledBy, setReconciledBy] = React.useState<string | null>(null);
  const [outsideDataRefreshedAt, setOutsideDataRefreshedAt] = React.useState<
    string | null
  >(null);
  type EditDraft = {
    medicationProduct: string;
    directions: string;
    indication: string;
    prescriber: string;
  };
  const [editingRowId, setEditingRowId] = React.useState<
    number | string | null
  >(null);
  const [editDraft, setEditDraft] = React.useState<EditDraft | null>(null);
  const [originalMedications, setOriginalMedications] = React.useState<any[]>(
    []
  );
  const [isAddMedicationOpen, setIsAddMedicationOpen] =
    React.useState<boolean>(false);

  type PendingMedication = {
    tempId: string;
    medication_product: string;
    value: string;
    directions: string;
    indication: string;
    diagnosis_code: string;
    prescriber: string;
    source: string;
  };
  const [pendingNewMedications, setPendingNewMedications] = React.useState<
    PendingMedication[]
  >([]);

  const [pendingAddCounter, setPendingAddCounter] = React.useState(0);
  const reconciledTableScrollRef = React.useRef<HTMLDivElement | null>(null);

  const [pendingDeletions, setPendingDeletions] = React.useState<any[]>([]);

  type PendingEdit = {
    medication_product: string;
    directions: string;
    value: string;
    indication: string;
    prescriber: string;
  };

  const [pendingEdits, setPendingEdits] = React.useState<
    Record<string, PendingEdit>
  >({});

  const [isMedicationHistoryOpen, setIsMedicationHistoryOpen] =
    React.useState<boolean>(false);

  const [currentSort, setCurrentSort] = React.useState<{ field: string; direction: "ascending" | "descending" }>({
    field: "medication_product",
    direction: "ascending",
  });
  const [outsideSort, setOutsideSort] = React.useState<{ field: string; direction: "ascending" | "descending" }>({
    field: "medication_name",
    direction: "ascending",
  });
  const { id: patientId } = useParams<{ id: string }>();

  const extractMedicationsArray = (payload: any): any[] => {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload.medications)) return payload.medications;
    if (payload.resource) return extractMedicationsArray(payload.resource);
    if (Array.isArray(payload.patient_medications)) return payload.patient_medications;
    if (Array.isArray(payload.data)) return payload.data;
    return [];
  };

  const extractReconciliationMeta = (payload: any) => {
    const sources = [payload, payload?.resource].filter(Boolean);
    let reconciledAtVal: string | null = null;
    let reconciledByVal: string | null = null;
    let outsideRefreshedAtVal: string | null = null;
    for (const src of sources) {
      if (!reconciledAtVal && src?.reconciled_at) reconciledAtVal = src.reconciled_at;
      if (!reconciledByVal && src?.reconciled_by) reconciledByVal = src.reconciled_by;
      if (!outsideRefreshedAtVal) {
        outsideRefreshedAtVal =
          src?.outside_medications_last_refreshed_at ||
          src?.outside_data_refreshed_at ||
          null;
      }
    }
    return { reconciledAtVal, reconciledByVal, outsideRefreshedAtVal };
  };

  const fetchPatientMedications = React.useCallback(() => {
    if (!patientId) return;
    fetch(`/patient_medications/${patientId}`, {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((result) => result.json())
      .then((result) => {
        const meds = extractMedicationsArray(result);
        setMedications(meds);

        const { reconciledAtVal, reconciledByVal, outsideRefreshedAtVal } =
          extractReconciliationMeta(result);
        setReconciledAt(reconciledAtVal);
        setReconciledBy(reconciledByVal);
        setOutsideDataRefreshedAt(outsideRefreshedAtVal);
      })
      .catch(() => {});
  }, [authenticationSetting.csrfToken, patientId]);

  const extractOutsideMedicationsArray = (payload: any): any[] => {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload.outside_medications)) return payload.outside_medications;
    if (payload.resource) return extractOutsideMedicationsArray(payload.resource);
    if (Array.isArray(payload.data)) return payload.data;
    return [];
  };

  const extractSearchedFlag = (payload: any): boolean => {
    if (!payload) return false;
    if (typeof payload.searched === "boolean") return payload.searched;
    if (payload.resource) return extractSearchedFlag(payload.resource);
    return false;
  };

  const fetchPatientOutsideMedications = React.useCallback(
    (searchTerm: string = "") => {
      if (!patientId) return;
      const trimmed = searchTerm.trim();
      const url = trimmed
        ? `/patient_outside_medications/${patientId}?search=${encodeURIComponent(
            trimmed
          )}`
        : `/patient_outside_medications/${patientId}`;
      fetch(url, {
        method: "GET",
        headers: getHeaders(authenticationSetting.csrfToken),
      })
        .then((result) => result.json())
        .then((result) => {
          setOutsideMedications(extractOutsideMedicationsArray(result));
          setOutsideMedicationsSearched(
            extractSearchedFlag(result) || trimmed.length > 0
          );
        })
        .catch(() => {});
    },
    [authenticationSetting.csrfToken, patientId]
  );

  React.useEffect(() => {
    fetchPatientMedications();
  }, [fetchPatientMedications]);

  // Debounced search effect: refetches the Outside Medication Data table
  // whenever the search field changes. Clearing the field (empty string)
  // restores the default list. Other patient profile sections are not
  // touched because this effect only updates `outsideMedications` state.
  React.useEffect(() => {
    const trimmed = outsideMedicationSearch.trim();
    if (trimmed.length > 0 && trimmed.length < 2) return;
    const handle = window.setTimeout(() => {
      fetchPatientOutsideMedications(outsideMedicationSearch);
    }, 300);
    return () => window.clearTimeout(handle);
  }, [outsideMedicationSearch, fetchPatientOutsideMedications]);

  const handleAddMedicationModalChange = (nextOpen: boolean) => {
    setIsAddMedicationOpen(nextOpen);
    if (nextOpen === false && !isReconciliationMode) {
      fetchPatientMedications();
      setHasReconciliationChanges(true);
    }
  };

  const handleLocalAddMedication = React.useCallback(
    (payload: {
      patient_id: number;
      medication_product: string;
      value: string;
      directions?: string;
      indication: string;
      diagnosis_code?: string;
      prescriber?: string;
      source: string;
    }) => {
      const tempId =
        typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
          ? crypto.randomUUID()
          : `pending_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      setPendingNewMedications((prev) => [
        ...prev,
        {
          tempId,
          medication_product: payload.medication_product,
          value: payload.value,
          directions: payload.directions ?? payload.value ?? "",
          indication: payload.indication,
          diagnosis_code: payload.diagnosis_code ?? "",
          prescriber: payload.prescriber ?? "",
          source: payload.source || "MANUAL",
        },
      ]);
      setHasReconciliationChanges(true);
      // Signal the effect to scroll the Reconciled list to the top so the
      // brand-new row is immediately visible to the user.
      setPendingAddCounter((c) => c + 1);
    },
    []
  );


  React.useEffect(() => {
    if (pendingAddCounter === 0) return;
    const scrollEl = reconciledTableScrollRef.current;
    if (!scrollEl) return;
    try {
      scrollEl.scrollTop = 0;
      scrollEl.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (_err) {
    }
  }, [pendingAddCounter]);

  const handleAddOutsideMedicationToList = React.useCallback(
    (record: any) => {
      if (!isReconciliationMode || !record) return;

      const medicationProduct =
        (record?.drug_product_description ||
          record?.medication_name ||
          "")
          .toString()
          .trim();

      const indication =
        (record?.diagnosis_description || record?.indication || "")
          .toString()
          .trim();
      const diagnosisCode = (record?.diagnosis_code || "").toString().trim();
      const prescriberName =
        (record?.prescriber?.name || "").toString().trim();

      handleLocalAddMedication({
        patient_id: Number(patientId),
        medication_product: medicationProduct,
        value: "",
        directions: "",
        indication,
        diagnosis_code: diagnosisCode,
        prescriber: prescriberName,
        source: "Claim",
      });
    },
    [handleLocalAddMedication, isReconciliationMode, patientId]
  );

  const removePendingMedication = (tempId: string) => {
    setPendingNewMedications((prev) =>
      prev.filter((m) => m.tempId !== tempId)
    );
  };

  const getSortIcon = (activeSort: { field: string; direction: "ascending" | "descending" }, column: string) =>
    activeSort.field === column ? (
      <span className="sortIndicator">
        {activeSort.direction === "ascending" ? (
          <img src={images.sort_ascending_src} width="10" className="sort-icon" alt="Sort Asc" />
        ) : (
          <img src={images.sort_descending_src} width="10" className="sort-icon" alt="Sort Desc" />
        )}
      </span>
    ) : (
      <span className="sortIndicator">
        <img src={images.sort_plain_src} width="10" className="sort-icon" alt="Sort" />
      </span>
    );

  const sortedMedications = React.useMemo(() => {

    const pendingRows = pendingNewMedications.map((p) => ({
      id: p.tempId,
      isPending: true,
      medication_product: p.medication_product,
      value: p.value,
      directions: p.directions,
      indication: p.indication,
      diagnosis_code: p.diagnosis_code,
      prescriber: p.prescriber,
      source: p.source,
      created_at: null,
    }));
    const pendingDeletionIds = new Set(
      pendingDeletions.map((m) => m?.id).filter((id) => id != null)
    );
    const visibleMedications = medications.filter(
      (m) => !pendingDeletionIds.has(m?.id)
    );
    const list = [...pendingRows, ...visibleMedications];
    list.sort((a, b) => {
      if (a.isPending && !b.isPending) return -1;
      if (!a.isPending && b.isPending) return 1;
      return (a.id || 0) > (b.id || 0) ? 1 : -1;
    });
    if (currentSort.field === "medication_product") {
      list.sort((a, b) => {
        if (a.isPending && !b.isPending) return -1;
        if (!a.isPending && b.isPending) return 1;
        return (a?.medication_product || "").toLowerCase() >
          (b?.medication_product || "").toLowerCase()
          ? 1
          : -1;
      });
    }
    if (currentSort.field === "created_at") {
      list.sort((a, b) => {
        if (a.isPending && !b.isPending) return -1;
        if (!a.isPending && b.isPending) return 1;
        return new Date(a?.created_at || 0) > new Date(b?.created_at || 0) ? 1 : -1;
      });
    }
    if (currentSort.direction === "descending") {
      const pinned = list.filter((m) => m.isPending);
      const rest = list.filter((m) => !m.isPending).reverse();
      return [...pinned, ...rest];
    }
    return list;
  }, [currentSort, medications, pendingNewMedications, pendingDeletions]);

  const showCurrentReconciledBadge = !isReconciliationMode && !!reconciledAt;

  const currentTableTitle = isReconciliationMode
    ? "Reconciled Medication List"
    : "Current Medications";
  const currentTableSubheader = isReconciliationMode
    ? "Validate your current medication's list below"
    : "Patient's current medication regimen confirmed through user review.";

  const formatReconciliationStamp = (iso: string | null): string | null => {
    if (!iso) return null;
    return `${formatToUsDateFromUTCNew(iso)} ${formatTimeWithAMPM(iso)}`;
  };
  const normalizeDirectionsDisplay = (raw: string | null | undefined): string => {
    const text = (raw || "").toString();
    if (!text) return text;
    return text
      .replace(/\s*\|\s*/g, ", ")
      .replace(/,\s*,/g, ", ")
      .replace(/\s+/g, " ")
      .trim();
  };

  const getDirections = (med: any): string => {
    if (med?.directions != null && med?.directions !== "") {
      return normalizeDirectionsDisplay(med.directions);
    }
    return normalizeDirectionsDisplay(med?.value) || "";
  };

  const handleStartEditRow = (med: any) => {
    if (!med || editingRowId != null) return; // single-row edit guard
    setEditDraft({
      medicationProduct: med?.medication_product || "",
      directions: getDirections(med),
      indication: med?.indication || "",
      prescriber: med?.prescriber || "",
    });
    setEditingRowId(med?.id);
  };

  const handleCancelEditRow = () => {
    setEditingRowId(null);
    setEditDraft(null);
  };

  const handleSaveEditRow = () => {
    if (editingRowId == null || !editDraft || !patientId) return;

    const trimmedDirections = (editDraft.directions || "").trim();

    const updatedFields = {
      medication_product: editDraft.medicationProduct,
      directions: trimmedDirections,
      value: trimmedDirections,
      indication: editDraft.indication,
      prescriber: editDraft.prescriber,
    };

    const pendingMatch = pendingNewMedications.find(
      (p) => p.tempId === editingRowId
    );
    if (pendingMatch) {
      setPendingNewMedications((prev) =>
        prev.map((p) =>
          p.tempId === editingRowId
            ? {
                ...p,
                medication_product: updatedFields.medication_product,
                value: updatedFields.value,
                directions: updatedFields.directions,
                indication: updatedFields.indication,
                prescriber: updatedFields.prescriber || p.prescriber,
              }
            : p
        )
      );
      setEditingRowId(null);
      setEditDraft(null);
      setHasReconciliationChanges(true);
      return;
    }

    setMedications((prev) =>
      prev.map((m) =>
        m?.id === editingRowId ? { ...m, ...updatedFields } : m
      )
    );
    setPendingEdits((prev) => ({
      ...prev,
      [String(editingRowId)]: updatedFields,
    }));
    setEditingRowId(null);
    setEditDraft(null);
    setHasReconciliationChanges(true);
  };
  const handleDeleteRow = async (med: any) => {
    if (!med || med?.id == null) return;


    if (med.isPending) {
      removePendingMedication(med.id);
      if (editingRowId === med.id) {
        setEditingRowId(null);
        setEditDraft(null);
      }
      return;
    }


    if (isReconciliationMode) {
      setPendingDeletions((prev) => {
        if (prev.some((m) => m?.id === med.id)) return prev;
        return [...prev, med];
      });

      setPendingEdits((prev) => {
        const key = String(med.id);
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
      if (editingRowId === med.id) {
        setEditingRowId(null);
        setEditDraft(null);
      }
      setHasReconciliationChanges(true);
      return;
    }
    const removedId = med.id;
    setMedications((prev) => prev.filter((m) => m?.id !== removedId));
    if (editingRowId === removedId) {
      setEditingRowId(null);
      setEditDraft(null);
    }
    setHasReconciliationChanges(true);
    try {
      const response = await fetch(
        `/patient_medications/${removedId}`,
        {
          method: "DELETE",
          headers: getHeaders(authenticationSetting.csrfToken),
        }
      );
      const result = await response.json().catch(() => null);
      if (result?.success === false) {
        setMedications((prev) => {
          if (prev.some((m) => m?.id === removedId)) return prev;
          return [med, ...prev];
        });
        return;
      }
      const serverList =
        result?.resource && Array.isArray(result.resource)
          ? result.resource
          : null;
      if (serverList) {
        setMedications(serverList);
      }
    } catch (error) {
      setMedications((prev) => {
        if (prev.some((m) => m?.id === removedId)) return prev;
        return [med, ...prev];
      });
    }
  };

  const startReconciliationMode = () => {
    setOriginalMedications(medications.map((m) => ({ ...m })));
    setIsReconciliationMode(true);
    setHasReconciliationChanges(true);

    if (!patientId) return;
    fetch(`/patient_medications/${patientId}/start_reconciliation`, {
      method: "POST",
      headers: getHeaders(authenticationSetting.csrfToken),
    }).catch(() => {
    });

    if (patientId) {
      fetch(`/patient_outside_medications/${patientId}/reconciliation`, {
        method: "GET",
        headers: getHeaders(authenticationSetting.csrfToken),
      }).catch(() => {});
    }
  };

  const exitReconciliationMode = () => {
    setMedications(originalMedications);
    setEditingRowId(null);
    setEditDraft(null);
    setIsReconciliationMode(false);
    setHasReconciliationChanges(false);
    setPendingNewMedications([]);
    setPendingDeletions([]);
    setPendingEdits({});
  };
  const openMedicationListHistory = () => {
    if (!patientId) return;
    setIsMedicationHistoryOpen(true);
  };

  const closeMedicationListHistory = () => {
    setIsMedicationHistoryOpen(false);
  };

  const applyMedicationsResource = (payload: any) => {
    setMedications(extractMedicationsArray(payload));
    const { reconciledAtVal, reconciledByVal, outsideRefreshedAtVal } =
      extractReconciliationMeta(payload);
    setReconciledAt(reconciledAtVal);
    setReconciledBy(reconciledByVal);
    setOutsideDataRefreshedAt(outsideRefreshedAtVal);
  };

  const saveReconciliationList = async () => {
    if (!hasReconciliationChanges || !patientId) return;
    try {
      for (const pending of pendingNewMedications) {
        const response = await fetch("/add_medication", {
          method: "POST",
          headers: getHeaders(authenticationSetting.csrfToken),
          body: JSON.stringify({
            medication: {
              patient_id: Number(patientId),
              medication_product: pending.medication_product,
              value: pending.value,
              directions: pending.directions,
              indication: pending.indication,
              diagnosis_code: pending.diagnosis_code,
              prescriber: pending.prescriber,
              source: pending.source || "MANUAL",
            },
          }),
        });
        if (!response.ok) {
          const body = await response.json().catch(() => null);
          if (body?.error) {
            // Surface the first failure and bail.
            return;
          }
        }
      }

      for (const removed of pendingDeletions) {
        if (!removed || removed.id == null) continue;
        const response = await fetch(
          `/patient_medications/${removed.id}?reconciliation=true`,
          {
            method: "DELETE",
            headers: getHeaders(authenticationSetting.csrfToken),
          }
        );
        if (!response.ok) {
          const body = await response.json().catch(() => null);
          if (body?.error) {
            return;
          }
        }
      }


      const deletedIds = new Set(
        pendingDeletions.map((m) => m?.id).filter((id) => id != null)
      );
      for (const [id, fields] of Object.entries(pendingEdits)) {
        if (deletedIds.has(Number(id)) || deletedIds.has(id)) continue;
        const response = await fetch(
          `/patient_medications/${patientId}/medications/${id}`,
          {
            method: "PATCH",
            headers: getHeaders(authenticationSetting.csrfToken),
            body: JSON.stringify({
              patient_medication: {
                medication_product: fields.medication_product,
                directions: fields.directions,
                value: fields.value,
                indication: fields.indication,
                prescriber: fields.prescriber,
              },
            }),
          }
        );
        if (!response.ok) {
          const body = await response.json().catch(() => null);
          if (body?.error) {
            return;
          }
        }
      }

      const beforeMedicationsPayload = originalMedications.map((m: any) => ({
        medication_product: m?.medication_product ?? m?.name ?? "",
        directions: m?.directions ?? "",
        value: m?.value ?? "",
        indication: m?.indication ?? "",
        prescriber: m?.prescriber ?? "",
        source: m?.source ?? "",
      }));

      const reconcileResult = await fetch(
        `/patient_medications/${patientId}/reconcile`,
        {
          method: "POST",
          headers: getHeaders(authenticationSetting.csrfToken),
          body: JSON.stringify({
            reconciliation: {
              before_medications: beforeMedicationsPayload,
            },
          }),
        }
      ).then((r) => r.json());

      if (reconcileResult?.success === false) {
        return;
      }
      applyMedicationsResource(reconcileResult);
      setIsReconciliationMode(false);
      setHasReconciliationChanges(false);
      setEditingRowId(null);
      setEditDraft(null);
      setOriginalMedications([]);
      setPendingNewMedications([]);
      setPendingDeletions([]);
      setPendingEdits({});
    } catch (_err) {
    }
  };
  const EMPTY_PLACEHOLDER = "-";

  const isPresent = (v: any): boolean =>
    v !== null && v !== undefined && `${v}`.trim() !== "";

  const formatOutsideFillDate = (raw: string | null | undefined): string => {
    if (!isPresent(raw)) return EMPTY_PLACEHOLDER;
    const formatted = formatToUsDateFromUTCNew(raw as string);
    return formatted && formatted !== "Invalid Date" ? formatted : EMPTY_PLACEHOLDER;
  };

  const formatOutsideQuantity = (raw: any): string => {
    if (!isPresent(raw)) return EMPTY_PLACEHOLDER;
    const num = Number(raw);
    if (Number.isNaN(num)) return `${raw}`;
    // Trim trailing zeros for a cleaner display (e.g. 60.000 -> 60).
    return num % 1 === 0 ? `${num}` : `${num}`.replace(/\.?0+$/, "");
  };

  const formatOutsideCopay = (raw: any): string => {
    if (!isPresent(raw)) return EMPTY_PLACEHOLDER;
    const num = Number(raw);
    if (Number.isNaN(num)) return `${raw}`;
    return `$${num.toFixed(2)}`;
  };

  const formatOutsidePhone = (raw: string | null | undefined): string => {
    if (!isPresent(raw)) return "";
    return `${raw}`.trim();
  };

  const renderPharmacyDetails = (record: any): React.ReactNode => {
    const pharmacy = record?.pharmacy || {};
    const name = isPresent(pharmacy.name) ? `${pharmacy.name}`.trim() : "";
    const city = isPresent(pharmacy.city) ? `${pharmacy.city}`.trim() : "";
    const state = isPresent(pharmacy.state) ? `${pharmacy.state}`.trim() : "";
    const phone = formatOutsidePhone(pharmacy.phone_number);

    const cityState = [city, state].filter(Boolean).join(", ");
    const lines = [name, cityState, phone].filter(Boolean);

    if (lines.length === 0) return EMPTY_PLACEHOLDER;

    return (
      <Stack spacing={0.25}>
        {lines.map((line, idx) => (
          <span key={idx}>{line}</span>
        ))}
      </Stack>
    );
  };

  // Prescriber Details cell -- groups name and city together and
  // gracefully skips missing pieces.
  const renderPrescriberDetails = (record: any): React.ReactNode => {
    const prescriber = record?.prescriber || {};
    const name = isPresent(prescriber.name) ? `${prescriber.name}`.trim() : "";
    const city = isPresent(prescriber.city) ? `${prescriber.city}`.trim() : "";
    const lines = [name, city].filter(Boolean);

    if (lines.length === 0) return EMPTY_PLACEHOLDER;

    return (
      <Stack spacing={0.25}>
        {lines.map((line, idx) => (
          <span key={idx}>{line}</span>
        ))}
      </Stack>
    );
  };

  const sortedOutsideMedications = React.useMemo(() => {
    const list = [...outsideMedications];
    if (outsideSort.field === "medication_name") {
      list.sort((a, b) =>
        (a?.drug_product_description || "").toString().toLowerCase() >
        (b?.drug_product_description || "").toString().toLowerCase()
          ? 1
          : -1
      );
    } else if (outsideSort.field === "fill_date") {
      list.sort(
        (a, b) =>
          new Date(a?.filled_date || 0).getTime() -
          new Date(b?.filled_date || 0).getTime()
      );
    }
    if (outsideSort.direction === "descending") {
      return list.reverse();
    }
    return list;
  }, [outsideMedications, outsideSort]);

  const refreshOutsideMedications = () => {
    if (!patientId) return;
    fetch(`/patient_medications/${patientId}/refresh_outside`, {
      method: "POST",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((result) => result.json())
      .then((result) => {
        if (result?.success === false) {
          return;
        }
        applyMedicationsResource(result);
        fetchPatientOutsideMedications(outsideMedicationSearch);
      })
      .catch(() => {});
  };

  return (
    <Box>
      <Grid container className="panel-container">
        <Grid item xs={12}>
          <Grid container className="panel-show-container">
            <Grid container className="panel-information-container">
              <Grid container direction="row" className="admin-header">
                <Grid item xs={12} className="box-header">
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    paddingX={3}
                    paddingY={2.3}
                    alignItems="flex-start"
                    display="flex"
                  >
                    <Stack spacing={0.5}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <h3 style={{ margin: 0 }}>{currentTableTitle}</h3>
                        {isReconciliationMode && (
                          <span
                            className="app-user-text"
                            style={{
                              backgroundColor: "#FFF0BE",
                              color: "#B08800",
                              borderRadius: 12,
                              padding: "3px 10px",
                              fontSize: 11,
                              lineHeight: 1.2,
                              whiteSpace: "nowrap",
                            }}
                          >
                            Reconciliation Mode Active
                          </span>
                        )}
                        {showCurrentReconciledBadge && (
                          <span
                            className="app-user-text"
                            style={{
                              backgroundColor: "#E6F7EE",
                              color: "#07BC2A",
                              borderRadius: 12,
                              padding: "3px 10px",
                              fontSize: 11,
                              lineHeight: 1.2,
                              fontWeight: 600,
                              whiteSpace: "nowrap",
                            }}
                          >
                            Last Updated: {formatReconciliationStamp(reconciledAt)}
                            {reconciledBy ? ` by ${reconciledBy}` : ""}
                          </span>
                        )}
                      </Stack>
                      <p style={subtextStyle}>{currentTableSubheader}</p>
                    </Stack>
                    {isReconciliationMode ? (
                      <Stack direction="row" spacing={2} alignItems="center" sx={{ alignSelf: "center" }}>
                        <Link
                          className="action-link row"
                          onClick={() => setIsAddMedicationOpen(true)}
                          aria-label="Add New Medication"
                          sx={{
                            backgroundColor: "transparent",
                            cursor: "pointer",
                            textDecoration: "none",
                          }}
                        >
                          <img
                            src={images?.add_icon}
                            alt=""
                            className="action-icon-image"
                            style={{ width: 31.466, maxHeight: 32 }}
                          />
                          <span
                            className="app-user-text disable-pointer"
                            style={{ marginLeft: "8px" }}
                          >
                            Add New Medication
                          </span>
                        </Link>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={saveReconciliationList}
                          disabled={!hasReconciliationChanges}
                        >
                          Save List
                        </Button>
                        <Link
                          component="button"
                          onClick={exitReconciliationMode}
                          underline="always"
                          sx={{
                            color: "#A29D9B",
                            fontFamily: "QuicksandMedium",
                            fontSize: "14px",
                            fontStyle: "normal",
                            fontWeight: 700,
                            lineHeight: "26px",
                            textDecorationLine: "underline",
                            textDecorationColor: "#A29D9B",
                            textDecorationStyle: "solid",
                            textDecorationSkipInk: "auto",
                            textDecorationThickness: "auto",
                            textUnderlineOffset: "auto",
                            textUnderlinePosition: "from-font",
                            width: "48px",
                            height: "26px",
                            minWidth: "48px",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            "&:hover": {
                              color: "#A29D9B",
                              textDecorationColor: "#A29D9B",
                            },
                          }}
                        >
                          Cancel
                        </Link>
                      </Stack>
                    ) : (
                      <Stack direction="row" spacing={2} alignItems="center" sx={{ alignSelf: "center" }}>
                        <Link
                          className="action-link row"
                          onClick={openMedicationListHistory}
                          aria-label="View Medication List History"
                          sx={{
                            backgroundColor: "transparent",
                            cursor: "pointer",
                          }}
                        >
                          <img
                            src={images?.clock_icon}
                            alt="View Medication List History"
                            className="action-icon-image"
                            style={{ width: 20, maxHeight: 20 }}
                          />
                          <span className="app-user-text">
                            View Medication List History
                          </span>
                        </Link>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={startReconciliationMode}
                        >
                          Start Reconciliation
                        </Button>
                      </Stack>
                    )}
                  </Stack>
                </Grid>
              </Grid>
              <Grid
                container
                className="medication-container grey-container"
                style={{ marginTop: "-11px" }}
                sx={isReconciliationMode ? { border: "1px solid #FFCC80" } : undefined}
              >
                <Grid item xs={12} className="medication-table-container" sx={{ mt: 1 }}>
                  <TableContainer
                    ref={reconciledTableScrollRef}
                    sx={{ maxHeight: TABLE_MAX_HEIGHT }}
                  >
                    <Table stickyHeader>
                      <TableHead className="table-header-box">
                        <TableRow>
                          <TableCell
                            className="nowrap-header"
                            style={{ width: 240, cursor: "pointer" }}
                            onClick={() =>
                              setCurrentSort((prev) => ({
                                field: "medication_product",
                                direction:
                                  prev.field === "medication_product" &&
                                  prev.direction === "ascending"
                                    ? "descending"
                                    : "ascending",
                              }))
                            }
                          >
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <p className="table-header" style={darkHeaderStyle}>Medication</p>
                              {getSortIcon(currentSort, "medication_product")}
                            </Stack>
                          </TableCell>
                          <TableCell style={{ width: 240 }}>
                            <p className="table-header" style={darkHeaderStyle}>Directions</p>
                          </TableCell>
                          <TableCell style={{ width: 370 }}>
                            <p className="table-header" style={darkHeaderStyle}>Indication</p>
                          </TableCell>
                          <TableCell style={{ width: 120 }}>
                            <p className="table-header" style={darkHeaderStyle}>Prescriber</p>
                          </TableCell>
                          <TableCell style={{ width: 123 }}>
                            <p className="table-header" style={darkHeaderStyle}>Source</p>
                          </TableCell>
                          {isReconciliationMode && (
                            <TableCell
                              className="nowrap-header"
                              style={{ width: 160 }}
                              align="right"
                            >
                              <p className="table-header" style={darkHeaderStyle}>Actions</p>
                            </TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedMedications.length > 0 ? (
                          sortedMedications.map((med) => {
                            const isEditing =
                              isReconciliationMode && editingRowId === med?.id;
                            const directionsText = getDirections(med);
                            const otherRowEditing =
                              editingRowId != null && editingRowId !== med?.id;

                            if (isEditing && editDraft) {
                              return (
                                <TableRow key={med?.id}>
                                  <TableCell>
                                    <TextField
                                      size="small"
                                      fullWidth
                                      value={editDraft.medicationProduct}
                                      sx={{background:"#EFE9E7"}}
                                      placeholder="Medication (RXNorm)"
                                      onChange={(e) =>
                                        setEditDraft((d) =>
                                          d
                                            ? { ...d, medicationProduct: e.target.value }
                                            : d
                                        )
                                      }
                                      inputProps={{
                                        "aria-label": "Edit medication name",
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <TextField
                                      size="small"
                                      fullWidth
                                      value={editDraft.directions}
                                      sx={{background:"#EFE9E7"}}
                                      placeholder="Directions"
                                      onChange={(e) => {
                                        const next = e.target.value;
                                        if (next.length > DIRECTIONS_MAX_LENGTH) return;
                                        setEditDraft((d) =>
                                          d ? { ...d, directions: next } : d
                                        );
                                      }}
                                      inputProps={{
                                        "aria-label": "Edit directions",
                                        maxLength: DIRECTIONS_MAX_LENGTH,
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <TextField
                                      size="small"
                                      fullWidth
                                      value={editDraft.indication}
                                      sx={{background:"#EFE9E7"}}
                                      placeholder="Indication"
                                      onChange={(e) =>
                                        setEditDraft((d) =>
                                          d
                                            ? {
                                                ...d,
                                                indication: e.target.value,
                                              }
                                            : d
                                        )
                                      }
                                      inputProps={{
                                        "aria-label": "Edit indication",
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <TextField
                                      size="small"
                                      fullWidth
                                      value={editDraft.prescriber}
                                      sx={{background:"#EFE9E7"}}
                                      placeholder="Prescriber"
                                      onChange={(e) =>
                                        setEditDraft((d) =>
                                          d
                                            ? {
                                                ...d,
                                                prescriber: e.target.value,
                                              }
                                            : d
                                        )
                                      }
                                      inputProps={{
                                        "aria-label": "Edit prescriber",
                                      }}
                                    />
                                  </TableCell>
                                  {/*
                                    Source is read-only at all times per the
                                    acceptance criteria, even while the rest
                                    of the row is in edit mode.
                                  */}
                                  <TableCell>
                                    {med?.source || "Added Manually"}
                                  </TableCell>
                                  <TableCell align="right">
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                      justifyContent="flex-end"
                                    >
                                      <Link
                                        component="button"
                                        type="button"
                                        underline="always"
                                        onClick={handleSaveEditRow}
                                        aria-label="Save row changes"
                                        sx={{
                                          color: "#0F62FE",
                                          textDecorationColor: "#0F62FE",
                                          fontFamily: "QuicksandMedium",
                                          fontSize: "13px",
                                          fontWeight: 700,
                                          "&:hover": {
                                            color: "#0F62FE",
                                            textDecorationColor: "#0F62FE",
                                          },
                                        }}
                                      >
                                        Save
                                      </Link>
                                      <Link
                                        component="button"
                                        type="button"
                                        underline="always"
                                        onClick={handleCancelEditRow}
                                        aria-label="Cancel row changes"
                                        sx={{
                                          color: "#0F62FE",
                                          textDecorationColor: "#0F62FE",
                                          fontFamily: "QuicksandMedium",
                                          fontSize: "13px",
                                          fontWeight: 700,
                                          "&:hover": {
                                            color: "#0F62FE",
                                            textDecorationColor: "#0F62FE",
                                          },
                                        }}
                                      >
                                        Cancel
                                      </Link>
                                      <Tooltip title="Remove medication">
                                        <IconButton
                                          size="small"
                                          aria-label="Remove medication"
                                          onClick={() => handleDeleteRow(med)}
                                          sx={{ color: "#4A4442" }}
                                        >
                                          <Delete sx={{ height: 24, color: "#1E1E1E" }} />
                                        </IconButton>
                                      </Tooltip>
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              );
                            }

                            // ── Read-only row in Reconciliation Mode ──────
                            if (isReconciliationMode) {
                              return (
                                <TableRow key={med?.id}>
                                  <TableCell>{med?.medication_product || "-"}</TableCell>
                                  <TableCell>{directionsText || "-"}</TableCell>
                                  <TableCell>
                                    {med?.indication || "-"}
                                  </TableCell>
                                  <TableCell>
                                    {med?.prescriber || "-"}
                                  </TableCell>
                                  <TableCell>
                                    {med?.source || "Added Manually"}
                                  </TableCell>
                                  <TableCell align="right">
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                      justifyContent="flex-end"
                                    >
                                      <Link
                                        component="button"
                                        type="button"
                                        underline="always"
                                        disabled={otherRowEditing}
                                        onClick={() => handleStartEditRow(med)}
                                        aria-label="Edit medication row"
                                        sx={{
                                          color: otherRowEditing
                                            ? "#A29D9B"
                                            : "#0F62FE",
                                          fontFamily: "QuicksandMedium",
                                          fontSize: "13px",
                                          fontWeight: 700,
                                          textDecorationColor: otherRowEditing
                                            ? "#A29D9B"
                                            : "#0F62FE",
                                          pointerEvents: otherRowEditing
                                            ? "none"
                                            : "auto",
                                          opacity: otherRowEditing ? 0.6 : 1,
                                        }}
                                      >
                                        Edit
                                      </Link>
                                      <Tooltip title="Remove medication">
                                        <span>
                                          <IconButton
                                            size="small"
                                            aria-label="Remove medication"
                                            disabled={otherRowEditing}
                                            onClick={() =>
                                              handleDeleteRow(med)
                                            }
                                            sx={{ color: "#4A4442" }}
                                          >
                                            <Delete sx={{ height: 24, color: "#1E1E1E" }} />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              );
                            }

                            // ── Default read-only row (no row actions) ────
                            return (
                              <TableRow key={med?.id}>
                                <TableCell>{med?.medication_product || "-"}</TableCell>
                                <TableCell>{directionsText || "-"}</TableCell>
                                <TableCell>{med?.indication || "-"}</TableCell>
                                <TableCell>{med?.prescriber || "-"}</TableCell>
                                <TableCell>
                                  {med?.source || "Added Manually"}
                                </TableCell>
                              </TableRow>
                            );
                          })
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={isReconciliationMode ? 6 : 5}
                              align="center"
                            >
                              No current medications available.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>


      <Grid container className="panel-container" sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Grid container className="panel-show-container">
            <Grid container className="panel-information-container">
              <Grid container direction="row" className="admin-header">
                <Grid item xs={12} className="box-header">
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    paddingX={3}
                    paddingY={2.3}
                    alignItems="flex-start"
                    display="flex"
                  >
                    <Stack spacing={0.5}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <h3 style={{ margin: 0 }}>Outside Medication Data</h3>
                        {outsideDataRefreshedAt && (
                          <span
                            className="app-user-text"
                            style={{
                              backgroundColor: "#E6F7EE",
                              color: "#07BC2A",
                              borderRadius: 12,
                              padding: "3px 10px",
                              fontSize: 11,
                              lineHeight: 1.2,
                              whiteSpace: "nowrap",
                            }}
                          >
                            Last Updated: {formatReconciliationStamp(outsideDataRefreshedAt)}
                          </span>
                        )}
                      </Stack>
                      <p style={subtextStyle}>
                        Claim-derived medication records for this patient.
                      </p>
                    </Stack>
                    <TextField
                      size="small"
                      placeholder="Search Medication"
                      value={outsideMedicationSearch}
                      onChange={(e) => setOutsideMedicationSearch(e.target.value)}
                      InputProps={{
                        disableUnderline: true,
                        style: { borderRadius: "4px", height: 42.25, backgroundColor: "#EFE9E7", width: 240 },
                        endAdornment: (
                          <InputAdornment position="end">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        alignSelf: "center",
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": { border: "none" },
                          "&:hover fieldset": { border: "none" },
                          "&.Mui-focused fieldset": { border: "none" },
                        },
                        "& .MuiInputBase-input": {
                          color: "#1E1E1E",
                          fontSize: 12,
                          fontWeight: 500,
                          "&::placeholder": { color: "#868382", opacity: 1 },
                        },
                      }}
                    />
                  </Stack>
                </Grid>
              </Grid>
              <Grid
                container
                className="medication-container grey-container"
                style={{ marginTop: "-11px" }}
              >
                <Grid item xs={12} className="medication-table-container" sx={{ mt: 1 }}>
                  <TableContainer sx={{ maxHeight: TABLE_MAX_HEIGHT }}>
                    <Table stickyHeader>
                      <TableHead className="table-header-box">
                        <TableRow>
                          <TableCell
                            className="nowrap-header"
                            style={{ cursor: "pointer", width: 110 }}
                            onClick={() =>
                              setOutsideSort((prev) => ({
                                field: "fill_date",
                                direction:
                                  prev.field === "fill_date" && prev.direction === "ascending"
                                    ? "descending"
                                    : "ascending",
                              }))
                            }
                          >
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <p className="table-header" style={darkHeaderStyle}>Fill Date</p>
                              {getSortIcon(outsideSort, "fill_date")}
                            </Stack>
                          </TableCell>
                          <TableCell
                            className="nowrap-header"
                            style={{ cursor: "pointer", width: 220 }}
                            onClick={() =>
                              setOutsideSort((prev) => ({
                                field: "medication_name",
                                direction:
                                  prev.field === "medication_name" && prev.direction === "ascending"
                                    ? "descending"
                                    : "ascending",
                              }))
                            }
                          >
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <p className="table-header" style={darkHeaderStyle}>Medication</p>
                              {getSortIcon(outsideSort, "medication_name")}
                            </Stack>
                          </TableCell>
                          <TableCell style={{ width: 70 }}>
                            <p className="table-header" style={darkHeaderStyle}>Qty</p>
                          </TableCell>
                          <TableCell style={{ width: 100 }}>
                            <p className="table-header" style={darkHeaderStyle}>Day Supply</p>
                          </TableCell>
                          <TableCell style={{ width: 100 }}>
                            <p className="table-header" style={darkHeaderStyle}>Drug Tier</p>
                          </TableCell>
                          <TableCell style={{ width: 90 }}>
                            <p className="table-header" style={darkHeaderStyle}>Copay</p>
                          </TableCell>
                          <TableCell style={{ width: 220 }}>
                            <p className="table-header" style={darkHeaderStyle}>Pharmacy Details</p>
                          </TableCell>
                          <TableCell style={{ width: 170 }}>
                            <p className="table-header" style={darkHeaderStyle}>Prescriber Details</p>
                          </TableCell>
                          <TableCell style={{ width: 130 }}>
                            <p className="table-header" style={darkHeaderStyle}>Diagnosis Code</p>
                          </TableCell>
                          <TableCell style={{ width: 150 }}>
                            <p className="table-header" style={darkHeaderStyle}>MPA Expiration Date</p>
                          </TableCell>
                          {isReconciliationMode && (
                            <TableCell
                              className="nowrap-header"
                              style={{ width: 140 }}
                              align="right"
                            >
                              <p className="table-header" style={darkHeaderStyle}>Actions</p>
                            </TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedOutsideMedications.length > 0 ? (
                          sortedOutsideMedications.map((record) => (
                            <TableRow key={record?.id}>
                              <TableCell>
                                {formatOutsideFillDate(record?.filled_date)}
                              </TableCell>
                              <TableCell>
                                {isPresent(record?.drug_product_description)
                                  ? record.drug_product_description
                                  : EMPTY_PLACEHOLDER}
                              </TableCell>
                              <TableCell>
                                {formatOutsideQuantity(record?.quantity)}
                              </TableCell>
                              <TableCell>
                                {isPresent(record?.day_supply)
                                  ? record.day_supply
                                  : EMPTY_PLACEHOLDER}
                              </TableCell>
                              <TableCell>
                                {isPresent(record?.drug_tier)
                                  ? record.drug_tier
                                  : EMPTY_PLACEHOLDER}
                              </TableCell>
                              <TableCell>
                                {formatOutsideCopay(record?.copay)}
                              </TableCell>
                              <TableCell>
                                {renderPharmacyDetails(record)}
                              </TableCell>
                              <TableCell>
                                {renderPrescriberDetails(record)}
                              </TableCell>
                              <TableCell>
                                {isPresent(record?.diagnosis_code)
                                  ? record.diagnosis_code
                                  : EMPTY_PLACEHOLDER}
                              </TableCell>
                              <TableCell>
                                {formatOutsideFillDate(record?.mpa_end_date)}
                              </TableCell>
                              {isReconciliationMode && (
                                <TableCell align="right">
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() =>
                                      handleAddOutsideMedicationToList(record)
                                    }
                                    aria-label="Add to Reconciled Medication List"
                                  >
                                    Add to List
                                  </Button>
                                </TableCell>
                              )}
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={isReconciliationMode ? 11 : 10}
                              align="center"
                            >
                              {(() => {
                                const isActiveSearch =
                                  outsideMedicationSearch.trim().length > 0 ||
                                  outsideMedicationsSearched;
                                const heading = isActiveSearch
                                  ? "No outside medication data found."
                                  : "No outside medication data available.";
                                const supporting = isActiveSearch
                                  ? "Try adjusting your search or clearing the search field."
                                  : "Medication records imported from outside claim files will appear here when available.";
                                return (
                                  <Stack spacing={0.5} alignItems="center">
                                    <span
                                      style={{
                                        fontFamily: "QuicksandBold",
                                        fontWeight: 700,
                                        color: "#1E1E1E",
                                      }}
                                    >
                                      {heading}
                                    </span>
                                    <span style={subtextStyle}>
                                      {supporting}
                                    </span>
                                  </Stack>
                                );
                              })()}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {isAddMedicationOpen && patientId && (
        <AddMedication
          patient_id={Number(patientId)}
          edit_medication={null}
          open={isAddMedicationOpen}
          setOpen={handleAddMedicationModalChange}
          onLocalSave={
            isReconciliationMode ? handleLocalAddMedication : undefined
          }
        />
      )}
      {patientId && (
        <MedicationListHistoryModal
          open={isMedicationHistoryOpen}
          patientId={patientId}
          onClose={closeMedicationListHistory}
          csrfToken={authenticationSetting.csrfToken}
        />
      )}
    </Box>
  );
};

export default Medications;
